import { useState, useEffect, FormEvent, ChangeEvent, KeyboardEvent } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where, getDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Edit2, Trash2, Plus, Package, Search, Tag, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string;
    email?: string | null;
    emailVerified?: boolean;
    isAnonymous?: boolean;
    tenantId?: string | null;
  }
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  category?: string;
  tags?: string[];
  vendorId?: string;
  vendorName?: string;
  isArchived?: boolean;
  stock?: number;
  maxOrderQuantity?: number;
  dimensions?: string;
  details?: string;
}

export function ProductsManager({ vendorId }: { vendorId?: string }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<Partial<Product>>({});
  const [message, setMessage] = useState<{ text: string, type: 'success' | 'error' } | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  const [confirmingAction, setConfirmingAction] = useState<{ id: string, type: 'archive' | 'unarchive' } | null>(null);
  
  // New states for formatting and filtering
  const [priceInput, setPriceInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilterTag, setSelectedFilterTag] = useState<string | null>(null);

  const handleFirestoreError = (error: unknown, operationType: OperationType, path: string | null) => {
    const errInfo: FirestoreErrorInfo = {
      error: error instanceof Error ? error.message : String(error),
      authInfo: {
        userId: auth.currentUser?.uid,
        email: auth.currentUser?.email,
        emailVerified: auth.currentUser?.emailVerified,
        isAnonymous: auth.currentUser?.isAnonymous,
        tenantId: auth.currentUser?.tenantId,
      },
      operationType,
      path
    };
    console.error('Firestore Error: ', JSON.stringify(errInfo));
    setMessage({ text: `Firestore Error: ${errInfo.error}`, type: 'error' });
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let q;
      if (vendorId) {
        q = query(collection(db, 'products'), where('vendorId', '==', vendorId));
      } else {
        q = collection(db, 'products');
      }
      const querySnapshot = await getDocs(q);
      const fetchedProducts = querySnapshot.docs.map(doc => {
        const data = doc.data() as any;
        return {
          id: doc.id,
          name: data.name,
          description: data.description,
          price: data.price,
          imageUrl: data.imageUrl,
          category: data.category,
          tags: data.tags,
          vendorId: data.vendorId,
          vendorName: data.vendorName,
          isArchived: data.isArchived || false,
          stock: data.stock,
          maxOrderQuantity: data.maxOrderQuantity,
          dimensions: data.dimensions,
          details: data.details
        } as Product;
      });
      setProducts(fetchedProducts);
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [vendorId]);

  const handlePriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
    if (parts.length === 2 && parts[1].length > 2) val = parts[0] + '.' + parts[1].slice(0, 2);
    setPriceInput(val);
  };

  const handleAddTag = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim().toLowerCase();
      if (newTag && !(currentProduct.tags || []).includes(newTag)) {
        setCurrentProduct({
          ...currentProduct,
          tags: [...(currentProduct.tags || []), newTag]
        });
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setCurrentProduct({
      ...currentProduct,
      tags: (currentProduct.tags || []).filter(t => t !== tagToRemove)
    });
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setMessage(null);
    try {
      const parsedPrice = Math.round(parseFloat(priceInput || '0') * 100);
      
      if (!currentProduct.name || !currentProduct.description || isNaN(parsedPrice) || parsedPrice < 0) {
        setMessage({ text: 'Error: Missing or invalid required fields', type: 'error' });
        return;
      }

      const productData: any = {
        name: currentProduct.name,
        description: currentProduct.description,
        price: parsedPrice,
        updatedAt: serverTimestamp(),
        ...(currentProduct.imageUrl && { imageUrl: currentProduct.imageUrl }),
        ...(currentProduct.category && { category: currentProduct.category }),
        ...(currentProduct.tags && currentProduct.tags.length > 0 && { tags: currentProduct.tags }),
        ...(currentProduct.stock !== undefined && { stock: currentProduct.stock }),
        ...(currentProduct.maxOrderQuantity !== undefined && { maxOrderQuantity: currentProduct.maxOrderQuantity }),
        ...(currentProduct.dimensions && { dimensions: currentProduct.dimensions }),
        ...(currentProduct.details && { details: currentProduct.details })
      };

      // Always set vendor info for new products or if missing
      const effectiveVendorId = vendorId || auth.currentUser?.uid;
      if (effectiveVendorId) {
        productData.vendorId = effectiveVendorId;
        // Fetch/Use vendor profile info
        const vendorDoc = await getDoc(doc(db, 'users', effectiveVendorId));
        if (vendorDoc.exists()) {
          const vendorData = vendorDoc.data();
          productData.vendorName = vendorData.storeName || vendorData.displayName || 'Local Vendor';
        }
      }

      if (currentProduct.id) {
        await updateDoc(doc(db, 'products', currentProduct.id), productData);
        setMessage({ text: 'Product updated successfully', type: 'success' });
      } else {
        productData.isArchived = false;
        productData.createdAt = serverTimestamp();
        await addDoc(collection(db, 'products'), productData);
        setMessage({ text: 'Product created successfully', type: 'success' });
      }
      setIsEditing(false);
      setCurrentProduct({});
      setPriceInput('');
      fetchProducts();
    } catch (error: any) {
      handleFirestoreError(error, OperationType.WRITE, 'products');
    }
  };

  const handleArchive = async (id: string, archive: boolean = true) => {
    setMessage(null);
    try {
      await updateDoc(doc(db, 'products', id), { isArchived: archive });
      setMessage({ text: `Product ${archive ? 'archived' : 'unarchived'} successfully`, type: 'success' });
      setConfirmingAction(null);
      fetchProducts();
    } catch (error: any) {
      handleFirestoreError(error, OperationType.UPDATE, `products/${id}`);
    }
  };

  const openEditor = (product?: Product) => {
    if (product) {
      setCurrentProduct(product);
      setPriceInput((product.price / 100).toString());
    } else {
      setCurrentProduct({ tags: [] });
      setPriceInput('');
    }
    setIsEditing(true);
    setMessage('');
    setTagInput('');
  };

  const allTags = Array.from(new Set(products.flatMap(p => p.tags || []))).sort();
  
  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTag = selectedFilterTag ? p.tags?.includes(selectedFilterTag) : true;
    const matchesTab = activeTab === 'active' ? !p.isArchived : p.isArchived;
    return matchesSearch && matchesTag && matchesTab;
  });

    if (isEditing) {
    return (
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
        <div className="p-8 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50 flex justify-between items-center">
          <h2 className="text-xl font-medium text-neutral-900 dark:text-white flex items-center gap-2 font-serif">
            <Package className="w-5 h-5 text-neutral-500 dark:text-neutral-400 stroke-[1.5]" />
            {currentProduct.id ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button 
            onClick={() => { setIsEditing(false); setCurrentProduct({}); setMessage(null); }}
            className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white"
          >
            Cancel
          </button>
        </div>
        <form onSubmit={handleSave} className="p-8 space-y-6">
          {message && (
            <div className={`p-4 rounded-xl text-sm font-medium border flex items-center gap-3 ${
              message.type === 'error' 
                ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-100 dark:border-red-900/50' 
                : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-100 dark:border-green-900/50'
            }`}>
              {message.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              {message.text}
            </div>
          )}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Name</label>
              <input
                type="text"
                required
                value={currentProduct.name || ''}
                onChange={(e) => setCurrentProduct({...currentProduct, name: e.target.value})}
                className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Description</label>
              <textarea
                required
                rows={3}
                value={currentProduct.description || ''}
                onChange={(e) => setCurrentProduct({...currentProduct, description: e.target.value})}
                className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Price</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400">$</span>
                <input
                  type="text"
                  required
                  value={priceInput}
                  onChange={handlePriceChange}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white"
                />
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1.5 italic">
                A 5% marketplace platform fee is deducted from all sales. You earn ${(parseFloat(priceInput || '0') * 0.95).toFixed(2)} on this product.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Stock Quantity (Optional)</label>
                <input
                  type="number"
                  min="0"
                  value={currentProduct.stock !== undefined ? currentProduct.stock : ''}
                  onChange={(e) => {
                    const newProduct = {...currentProduct, stock: e.target.value === '' ? undefined : parseInt(e.target.value, 10)};
                    if (newProduct.stock !== undefined) {
                      newProduct.maxOrderQuantity = undefined; // clear max if stock is set
                    }
                    setCurrentProduct(newProduct);
                  }}
                  placeholder="Unlimited if left empty"
                  className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Max Pre-Order Quantity (If unlimited)</label>
                <input
                  type="number"
                  min="1"
                  disabled={currentProduct.stock !== undefined}
                  value={currentProduct.maxOrderQuantity !== undefined ? currentProduct.maxOrderQuantity : ''}
                  onChange={(e) => setCurrentProduct({...currentProduct, maxOrderQuantity: e.target.value === '' ? undefined : parseInt(e.target.value, 10)})}
                  placeholder={currentProduct.stock !== undefined ? "Not applicable" : "Default is 5"}
                  className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed text-neutral-900 dark:text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Image (Optional)</label>
              <div className="flex items-center gap-4">
                {currentProduct.imageUrl && (
                  <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0 border border-neutral-200 dark:border-neutral-800">
                    <img src={currentProduct.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const img = new Image();
                          img.onload = () => {
                            const canvas = document.createElement('canvas');
                            let width = img.width;
                            let height = img.height;
                            
                            // Max dimensions to ensure base64 isn't too huge (e.g. 600x600 for product previews)
                            const MAX_SIZE = 600;
                            if (width > height) {
                              if (width > MAX_SIZE) {
                                height *= MAX_SIZE / width;
                                width = MAX_SIZE;
                              }
                            } else {
                              if (height > MAX_SIZE) {
                                width *= MAX_SIZE / height;
                                height = MAX_SIZE;
                              }
                            }
                            
                            canvas.width = Math.floor(width);
                            canvas.height = Math.floor(height);
                            const ctx = canvas.getContext('2d');
                            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
                            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                            setCurrentProduct({ ...currentProduct, imageUrl: dataUrl });
                          };
                          img.src = event.target?.result as string;
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full px-4 py-2 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-neutral-100 file:text-neutral-700 hover:file:bg-neutral-200 dark:file:bg-neutral-800 dark:file:text-neutral-300"
                  />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Category (Optional)</label>
              <input
                type="text"
                value={currentProduct.category || ''}
                onChange={(e) => setCurrentProduct({...currentProduct, category: e.target.value})}
                className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white"
                placeholder="e.g. Apparel, Accessories"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Dimensions (Optional)</label>
              <input
                type="text"
                value={currentProduct.dimensions || ''}
                onChange={(e) => setCurrentProduct({...currentProduct, dimensions: e.target.value})}
                className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white"
                placeholder="e.g. 10x10x5 inches"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Details (Optional)</label>
              <textarea
                rows={3}
                value={currentProduct.details || ''}
                onChange={(e) => setCurrentProduct({...currentProduct, details: e.target.value})}
                className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white"
                placeholder="Materials, weight, and other specifics..."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Tags (Press Enter or comma to add)</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {(currentProduct.tags || []).map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-900 dark:hover:text-white">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                placeholder="e.g. summer, sale, new"
                className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white"
              />
            </div>
          </div>
          <div className="pt-4 flex justify-end gap-4">
            <button
              onClick={() => { setIsEditing(false); setCurrentProduct({}); setMessage(null); }}
              type="button"
              className="px-8 py-3.5 rounded-xl text-sm font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-white hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-8 py-3.5 rounded-xl text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors shadow-sm"
            >
              Save Product
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden flex flex-col shadow-sm">
      <div className="p-8 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-medium text-neutral-900 dark:text-white flex items-center gap-2 font-serif">
            <Package className="w-5 h-5 text-neutral-500 dark:text-neutral-400 stroke-[1.5]" />
            Products
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 font-light">Manage your store's inventory.</p>
        </div>
        <button
          onClick={() => openEditor()}
          className="flex items-center gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-4 py-2 rounded-lg text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors shrink-0 shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </button>
      </div>

      {message && (
        <div className="px-8 pt-6">
          <div className={`p-4 rounded-xl text-sm font-medium border flex items-center gap-3 ${
            message.type === 'error' 
              ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-100 dark:border-red-900/50' 
              : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-100 dark:border-green-900/50'
          }`}>
            {message.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
            {message.text}
          </div>
        </div>
      )}

      {/* Confirmation Modal Overlay */}
      {confirmingAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 dark:bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl max-w-sm w-full p-8 animate-in fade-in zoom-in duration-200 border border-neutral-100 dark:border-neutral-800">
            <div className="flex items-center gap-3 text-neutral-900 dark:text-white mb-4">
              <AlertCircle className="w-6 h-6 text-neutral-400 dark:text-neutral-500" />
              <h3 className="text-lg font-medium font-serif">Confirm Action</h3>
            </div>
            <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-8 leading-relaxed">
              Are you sure you want to {confirmingAction.type} this product? 
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmingAction(null)}
                className="flex-1 px-4 py-2.5 border border-neutral-200 dark:border-neutral-700 rounded-xl text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleArchive(confirmingAction.id, confirmingAction.type === 'archive');
                }}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white dark:text-neutral-900 transition-colors bg-neutral-900 dark:bg-white hover:bg-neutral-800 dark:hover:bg-neutral-100"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-8">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-6 py-4 text-sm font-medium transition-colors relative ${
            activeTab === 'active' ? 'text-neutral-900 dark:text-white' : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
          }`}
        >
          Active Products
          {activeTab === 'active' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-900 dark:bg-white" />}
        </button>
        <button
          onClick={() => setActiveTab('archived')}
          className={`px-6 py-4 text-sm font-medium transition-colors relative ${
            activeTab === 'archived' ? 'text-neutral-900 dark:text-white' : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
          }`}
        >
          Archived Items
          {activeTab === 'archived' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-900 dark:bg-white" />}
        </button>
      </div>
      
      <div className="p-6 border-b border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 dark:text-neutral-500" />
          <input
            type="text"
            placeholder="Search products by name or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white transition-colors text-sm bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
          />
        </div>
        
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            <Tag className="w-4 h-4 text-neutral-400 dark:text-neutral-500 mr-1" />
            <button
              onClick={() => setSelectedFilterTag(null)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                selectedFilterTag === null 
                  ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm' 
                  : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700'
              }`}
            >
              All
            </button>
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedFilterTag(tag)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  selectedFilterTag === tag 
                    ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm' 
                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {loading ? (
        <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">Loading products...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">No products match your search.</div>
      ) : (
        <div className="overflow-x-auto bg-white dark:bg-neutral-900">
          <table className="w-full text-left text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-800/50 border-b border-neutral-100 dark:border-neutral-800 text-neutral-500 dark:text-neutral-400">
              <tr>
                <th className="px-6 py-4 font-medium">Product</th>
                <th className="px-6 py-4 font-medium">Price</th>
                <th className="px-6 py-4 font-medium">Stock</th>
                <th className="px-6 py-4 font-medium">Tags</th>
                <th className="px-6 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {filteredProducts.map((product) => (
                <tr key={product.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded bg-neutral-100 dark:bg-neutral-800 overflow-hidden shrink-0">
                        <img src={product.imageUrl || 'https://picsum.photos/seed/product/100/100'} alt={`Handmade ${product.name} by ${product.vendorName || 'Local Artisan'} on Cardston Market`} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div>
                        <div className="font-medium text-neutral-900 dark:text-white flex items-center gap-2">
                           {product.name}
                           {!product.isArchived && (
                              <button
                                 onClick={async () => {
                                    try {
                                       await updateDoc(doc(db, 'products', product.id), { createdAt: serverTimestamp() });
                                       toast.success('Listing bumped!');
                                    } catch(e) {
                                       toast.error('Failed to bump listing.');
                                    }
                                 }}
                                 className="text-[10px] bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                                 title="Bump to top of recent listings"
                              >
                                 Bump
                              </button>
                           )}
                        </div>
                        <div className="text-neutral-500 dark:text-neutral-400 text-xs truncate max-w-xs">{product.description}</div>
                        {product.category && (
                          <div className="text-neutral-400 dark:text-neutral-500 text-[10px] uppercase tracking-wider mt-0.5">{product.category}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-neutral-900 dark:text-white font-medium">
                    ${(product.price / 100).toFixed(2)}
                  </td>
                  <td className="px-6 py-4">
                    {product.stock !== undefined ? (
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        product.stock > 0 
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' 
                          : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                      }`}>
                        {product.stock}
                      </span>
                    ) : (
                      <span className="text-neutral-400 dark:text-neutral-500 text-xs italic">Unlimited</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(product.tags || []).slice(0, 3).map(tag => (
                        <span key={tag} className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
                          {tag}
                        </span>
                      ))}
                      {(product.tags || []).length > 3 && (
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
                          +{(product.tags || []).length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditor(product)}
                        className="p-2 text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                        title="Edit Product"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setConfirmingAction({ id: product.id, type: product.isArchived ? 'unarchive' : 'archive' })}
                        className="p-2 text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                        title={product.isArchived ? "Unarchive Product" : "Archive Product"}
                      >
                        {product.isArchived ? <Plus className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
